import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import CanonicalTag from '@/components/CanonicalTag';
import twoStoryADUImage from '@/assets/two-story-adu.jpg';

const CanYouBuildTwoStoryADUCalifornia = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  return (
    <>
      <CanonicalTag url="https://www.udaadu.com/2-story-adu-california" />
      
      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative bg-white rounded-lg w-full max-w-4xl h-full max-h-[90vh]">
            <button 
              onClick={closeCalendlyModal} 
              className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
            >
              ✕
            </button>
            <iframe 
              src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" 
              width="100%" 
              height="100%" 
              frameBorder="0" 
              title="Schedule a meeting"
            />
          </div>
        </div>
      )}
      
      <div className="min-h-screen bg-background">
        <Navigation />
        
        <main className="container mx-auto px-4 py-8">
          {/* Hero Section */}
          <div className="mb-12">
            <img 
              src={twoStoryADUImage} 
              alt="Two-story ADU with height measurements" 
              className="w-full h-64 md:h-96 object-cover rounded-lg mb-8"
            />
            
            <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#E4683B' }}>
              Can You Build a 2-Story ADU in California?
            </h1>
            
            <p className="text-lg md:text-xl text-white leading-relaxed mb-8">
              Learn about the feasibility and regulations for building a two-story ADU in California. Find out city-specific height and zoning rules clearly explained.
            </p>
          </div>

          {/* Content Sections */}
          <div className="space-y-12">
            <section>
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#E4683B' }}>
                California Guidelines for Two-Story ADUs
              </h2>
              <p className="text-white leading-relaxed mb-6">
                California state law permits two-story ADUs subject to local height restrictions and building codes. Most jurisdictions allow multi-story ADUs within standard residential height limits, typically 16-25 feet depending on zoning district and property characteristics. Two-story designs maximize square footage while maintaining compliance with setback requirements.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#E4683B' }}>
                Height and Size Limitations
              </h2>
              <p className="text-white leading-relaxed mb-6">
                Two-story ADUs must comply with maximum height restrictions, typically ranging from 16 feet in some residential zones to 25 feet in others. Total square footage remains subject to standard ADU size limits (up to 1,200 sq ft for detached units), but vertical construction allows more efficient use of limited lot space while maintaining required setbacks.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#E4683B' }}>
                City-specific Rules for Multi-story ADUs
              </h2>
              <p className="text-white leading-relaxed mb-6">
                Cities like Los Angeles, San Diego, and San Jose each maintain specific height limits and design standards for two-story ADUs. Some jurisdictions require additional architectural review for multi-story structures, while others streamline approval processes. Local fire safety codes may impose additional stairway and egress requirements for second-story units.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#E4683B' }}>
                Frequently Asked Questions
              </h2>
              <div className="text-white leading-relaxed space-y-6">
                <div>
                  <p className="font-semibold mb-2">Q: Are two-story ADUs allowed statewide in California?</p>
                  <p>A: Yes, but city zoning codes may impose additional height and size restrictions.</p>
                </div>
                
                <div>
                  <p className="font-semibold mb-2">Q: What are typical height limits for two-story ADUs?</p>
                  <p>A: Typically 16-25 feet depending on local zoning and property characteristics.</p>
                </div>
              </div>
            </section>
          </div>

          {/* Call-to-Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-12">
            <Button 
              onClick={openCalendlyModal}
              className="bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-3 text-lg"
            >
              Book & Schedule
            </Button>
            <Button 
              asChild
              variant="outline"
              className="border-primary text-primary hover:bg-primary hover:text-primary-foreground px-8 py-3 text-lg"
            >
              <Link to="/landing">Learn More</Link>
            </Button>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default CanYouBuildTwoStoryADUCalifornia;